import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import {
    AccessRightFilterCollectionModel,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
    ILightWeightArea,
    ILightWeightObject,
    ILightWeightUser,
    ILocalSearchDataItemModel,
    LocalSearchDataModel,
    SelectionFilterModel,
} from '@fi-shared/models';
import { ModuleType } from '@fi-shared/types/module-type';
import { LocalSearchItemModel } from '@fi-shared/models';
import { ISelectionFilterUserSettings } from '@fi-shared/access-right-filters';
import { ObjectTypes } from '@fi-shared/types';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { isDefined } from '@fi-shared/utils';

@Injectable()
export class AccessRightService {
    constructor(private fiBackend: FiBackendService) {}

    getAccessRightsForPublicationByObjectType(objectType: ObjectTypes) {
        return this.fiBackend.fetchData<AccessRightFilterCollectionModel>(
            `Core/AccessRight/GetAccessRightsForPublicationByObjectType/${objectType}`,
        );
    }

    getAccessRightsForRequestAccessSettings() {
        return this.fiBackend.fetchData<AccessRightFilterCollectionModel>(
            `Core/AccessRight/GetAccessRightsForRequestAccessSettings`,
        );
    }

    GetExtendedAccessRightsForPublication() {
        return this.fiBackend.fetchData<AccessRightFilterCollectionModel>(
            'Core/AccessRight/GetExtendedAccessRightsForPublication',
        );
    }

    GetBusinessUnitsForUserProfile() {
        return this.fiBackend.fetchData<ILightWeightObject[]>('Core/AccessRight/GetBusinessUnitsForUserProfile');
    }

    GetBusinessAreasForBusinessUnit() {
        return this.fiBackend.fetchData('Core/AccessRight/GetBusinessAreasForBusinessUnit');
    }

    GetAreasForBusinessUnit() {
        return this.fiBackend.fetchData('Core/AccessRight/GetAreasForBusinessUnit');
    }

    GetClustersForBusinessUnit() {
        return this.fiBackend.fetchData('Core/AccessRight/GetClustersForBusinessUnit');
    }

    GetAccessRightsForUser(applicationLanguageType?: FiApplicationLanguageType) {
        let url = 'Core/AccessRight/GetAccessRightsForUser';
        if (isDefined(applicationLanguageType)) {
            url += `?applicationLanguageType=${applicationLanguageType}`;
        }
        return this.fiBackend.fetchData<AccessRightFilterCollectionModel>(url);
    }

    GetAccessRightsForRequestAccess() {
        return this.fiBackend.fetchData<AccessRightFilterCollectionModel>(
            'Core/AccessRight/GetAccessRightsForRequestAccess',
        );
    }

    GetSelectionFiltersForPublication(moduleType, includeInactiveFilters) {
        return this.fiBackend.fetchData<IAccessRightSelectionFiltersCollection[]>(
            `Core/AccessRight/GetSelectionFiltersForPublication/${moduleType}/${includeInactiveFilters}`,
        );
    }

    SaveSelectionFilter(selectionFilterApiModel: SelectionFilterModel, moduleType: ModuleType) {
        return this.fiBackend.fetchDataPost<number>(
            'Core/AccessRight/SaveSelectionFilter/' + moduleType,
            selectionFilterApiModel,
        );
    }

    DeleteSelectionFilter(selectionFilterId: number, moduleType = ModuleType.Core) {
        return this.fiBackend.fetchDataPost('Core/AccessRight/DeleteSelectionFilter', {
            ObjectId: selectionFilterId,
            ModuleType: moduleType,
        });
    }

    AreAccessRightFilterCollectionEqual(
        accessRightFilterCollection: IAccessRightFilterCollection,
        selectionFilterSettingCollection: AccessRightFilterCollectionModel,
        areaIdsWithChildren: number[],
    ) {
        var IsArraysEqual = function (Array1, Array2) {
            if (Array1.length != Array2.length) {
                return false;
            }
            for (var i = 0; i < Array1.length; i++) {
                if (Array2.indexOf(Array1[i]) == -1) {
                    return false;
                }
            }
            return true;
        };

        // ignore areas with children when compering
        // https://chainformation.atlassian.net/browse/FI-2351
        var areaIds = accessRightFilterCollection.AccessRightCoreFilter.AreaIds.filter(
            (areaId) => areaIdsWithChildren.indexOf(areaId) === -1,
        );
        var selectionFilterAreaIds = selectionFilterSettingCollection.AccessRightCoreFilter.AreaIds.filter(
            (areaId) => areaIdsWithChildren.indexOf(areaId) === -1,
        );

        return (
            IsArraysEqual(
                accessRightFilterCollection.AccessRightCoreFilter.RoleIds,
                selectionFilterSettingCollection.AccessRightCoreFilter.RoleIds,
            ) &&
            IsArraysEqual(
                accessRightFilterCollection.AccessRightCoreFilter.BusinessAreaIds,
                selectionFilterSettingCollection.AccessRightCoreFilter.BusinessAreaIds,
            ) &&
            IsArraysEqual(areaIds, selectionFilterAreaIds) &&
            IsArraysEqual(
                accessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds,
                selectionFilterSettingCollection.AccessRightCoreFilter.BusinessUnitIds,
            ) &&
            IsArraysEqual(
                accessRightFilterCollection.AccessRightCoreFilter.ClusterIds,
                selectionFilterSettingCollection.AccessRightCoreFilter.ClusterIds,
            ) &&
            IsArraysEqual(
                accessRightFilterCollection.AccessRightUserFilter.UserIds,
                selectionFilterSettingCollection.AccessRightUserFilter.UserIds,
            )
        );
    }

    SetAccessRightCoreFilter(ids, dataSet) {
        dataSet.forEach(function (item) {
            item.Checked = ids.indexOf(item.Id) !== -1;
        });
    }

    SetAccessRightCoreFilterForSearchItems(ids, objectType, selectedDataSet, fullDataSet) {
        var searchItems = this.GetSelectedSearchItemsCollection(ids, objectType, fullDataSet);

        searchItems.forEach(function (searchItem) {
            selectedDataSet.push(searchItem);
        });
    }

    GetSelectedSearchItemsCollection(itemIds: number[], objectType, fullDataSet) {
        var selectedSearchItems = [];

        itemIds.forEach(function (itemId) {
            var item = fullDataSet.Data.filter(function (item) {
                return item.Id == itemId;
            });

            if (item.length > 0) selectedSearchItems.push(item[0]);
        });

        return selectedSearchItems;
    }

    AddReadOnlyItemsForSearchItems<
        T extends {
            Id: number;
        },
    >(items: T[], objectType, fullDataSet: LocalSearchDataModel, itemNameAccessor: (item: T) => string) {
        const result: number[] = [];
        if (!items) return result;
        items.forEach(function (item) {
            const existingItem = fullDataSet.Data.filter((dataItem) => dataItem.Id === item.Id);
            if (!existingItem.length) {
                const name = itemNameAccessor(item);
                const ccmSearchItem = new LocalSearchItemModel(item.Id, name, objectType);
                fullDataSet.Data.push(ccmSearchItem);
                result.push(item.Id);
            }
        });
        return result;
    }

    GetEditAccessData(
        items: {
            ObjectId: number;
            ObjectType: ObjectTypes;
            ForumId: number;
            CreatedByUserId: number;
        }[],
    ) {
        const data = items.map(function (item) {
            return {
                ObjectId: item.ObjectId,
                ObjectType: item.ObjectType,
                ForumId: item.ForumId,
                CreatedByUserId: item.CreatedByUserId,
            };
        });
        return this.fiBackend.fetchDataPost<
            {
                ObjectId: number;
                ObjectType: ObjectTypes;
                HasEditAccess: boolean;
                HasDeleteAccess: boolean;
            }[]
        >('Core/AccessRight/GetEditAccessData', data);
    }

    UpdateSelectionFilterIsDefault(id, moduleType, isDefault) {
        return this.fiBackend.fetchDataPost('Core/AccessRight/UpdateSelectionFilterIsDefault', {
            selectionFilterId: id,
            moduleType: moduleType,
            isDefault: isDefault,
        });
    }

    getSelectionFilterUserSettings() {
        return this.fiBackend.fetchData<ISelectionFilterUserSettings>(
            'Core/AccessRight/GetSelectionFilterUserSettings',
        );
    }

    saveSelectionFilterUserSettings(settings: ISelectionFilterUserSettings) {
        return this.fiBackend.fetchDataPost('Core/AccessRight/SaveSelectionFilterUserSettings', settings);
    }

    setDontShowNewQuickSelectionMessage() {
        return this.fiBackend.fetchDataPost('Core/AccessRight/SetDontShowNewQuickSelectionMessage');
    }

    removeNonExistingSelectionFilter(
        selectionFilterSettingCollection: AccessRightFilterCollectionModel,
        availableAccessRightFilterCollection: IAccessRightFilterCollection,
    ) {
        this.removeNonExistingIds(
            selectionFilterSettingCollection.AccessRightCoreFilter.RoleIds,
            availableAccessRightFilterCollection.AccessRightCoreFilter.RoleIds,
        );
        this.removeNonExistingIds(
            selectionFilterSettingCollection.AccessRightCoreFilter.BusinessAreaIds,
            availableAccessRightFilterCollection.AccessRightCoreFilter.BusinessAreaIds,
        );
        this.removeNonExistingIds(
            selectionFilterSettingCollection.AccessRightCoreFilter.AreaIds,
            availableAccessRightFilterCollection.AccessRightCoreFilter.AreaIds,
        );
        this.removeNonExistingIds(
            selectionFilterSettingCollection.AccessRightCoreFilter.ClusterIds,
            availableAccessRightFilterCollection.AccessRightCoreFilter.ClusterIds,
        );
        this.removeNonExistingIds(
            selectionFilterSettingCollection.AccessRightUserFilter.UserIds,
            availableAccessRightFilterCollection.AccessRightUserFilter.UserIds,
        );
    }

    private removeNonExistingIds(currentIds: number[], availableIds: number[]) {
        const idsToRemove = currentIds.filter((currentId) => availableIds.indexOf(currentId) === -1);
        idsToRemove.forEach((currentId) => currentIds.splice(currentIds.indexOf(currentId), 1));
    }
}
